"use client";

import { ComedianWithImageUrl, FeedPost, FeedPostType } from "./types";
import { FeedEvents, trackEvent } from "@/analytics";
import { SpecialRow, SpecialWithUnlocked } from "@/data/specials";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Comedian } from "@/data/comedians";
import ContinueWatchingFeedCarousel from "./ContinueWatchingFeedCarousel";
import FeaturedComedianFeedCard from "./cards/FeaturedComedianFeedCard";
import { cn } from "@/utils/cn";
import SuggestedComedians from "./SuggestedComedians";
import TrendingSpecialsFeed from "./TrendingSpecialsFeedCarousel";
import UpcomingShowsFeedUnitV2 from "./UpcomingShowsFeedUnitV2";
import VideoFeedCard from "./cards/VideoFeedCard";
import VideoFeedCardSkeleton from "./cards/VideoFeedCardSkeleton";
import { logException } from "@/analytics/sentry";
import { useExperiment, useGate } from "statsig-react";

interface FeedPostsProps {
  isLoggedOutState: boolean;
  /**
   * True when the logged-out brand moment is on (feed_brand_moment gate) — the
   * end of the feed becomes a conversion card instead of "No more posts".
   */
  brandMomentEnabled?: boolean;
  feedPosts: FeedPost[];
  suggestedComedians: ComedianWithImageUrl[];
  comedians: { [comedianId: string]: Comedian };
  isLoading?: boolean;
  continueWatchingSpecials?: {
    last_playback_location: number;
    special: SpecialRow;
    comedian: Comedian;
  }[];
  trendingSpecials?: SpecialWithUnlocked[];
}

export default function FeedPosts({
  isLoggedOutState,
  brandMomentEnabled = false,
  feedPosts,
  comedians,
  suggestedComedians,
  isLoading = false,
  continueWatchingSpecials,
  trendingSpecials,
}: FeedPostsProps) {
  const { value: isMobileVideoLayoutEnabled } = useGate("mobile_video_layout");
  const [videoCardPositions, setVideoCardPositions] = useState<{
    [postId: string]: { intersectionRatio: number; top: number };
  }>({});
  const [videosMuted, setVideosMuted] = useState<boolean>(true);
  const [postsToShow, setPostsToShow] = useState<number>(10);
  const pageBottomRef = useRef<HTMLDivElement>(null);

  const [locallyHiddenIds, setLocallyHiddenIds] = useState<Set<string>>(
    new Set()
  );
  const [locallyHiddenComedianIds, setLocallyHiddenComedianIds] = useState<
    Set<string>
  >(new Set());
  const handlePostHide = useCallback(
    (postId: string) => {
      setLocallyHiddenIds((prev) => new Set(prev).add(postId));
      const post = feedPosts.find((p) => p.id === postId);
      if (post?.type === FeedPostType.COMEDIAN && post.comedian?.id) {
        setLocallyHiddenComedianIds((prev) =>
          new Set(prev).add(post.comedian!.id)
        );
      }
    },
    [feedPosts]
  );

  const handleComedianHide = useCallback((comedianId: string) => {
    setLocallyHiddenComedianIds((prev) => new Set(prev).add(comedianId));
  }, []);

  const { config: hideSpecialsConfig } = useExperiment("feed_hide_specials");
  const hideEnabled = hideSpecialsConfig?.get("enabled", false);

  // Keep track of the actual feedPosts array reference to reset postsToShow on filter change
  const feedPostsRef = useRef(feedPosts);

  // Derived posts after local hides
  const displayedPosts = useMemo(
    () =>
      feedPosts.filter(
        (p) =>
          !locallyHiddenIds.has(p.id) &&
          !(
            p.type === FeedPostType.COMEDIAN &&
            p.comedian &&
            locallyHiddenComedianIds.has(p.comedian.id)
          )
      ),
    [feedPosts, locallyHiddenIds, locallyHiddenComedianIds]
  );

  const handleMutedChange = useCallback(
    (value: boolean) => setVideosMuted(value),
    []
  );
  const { config: nearbyShowsConfig } = useExperiment("nearby_shows_toggle");

  // Reset postsToShow when the filter changes (indicated by feedPosts array changing)
  useEffect(() => {
    if (feedPosts !== feedPostsRef.current) {
      setPostsToShow(10); // Reset to initial count when feed list changes
      feedPostsRef.current = feedPosts;
      // TODO Scroll to top might be desirable here too
      // window.scrollTo(0, 0);
    }
  }, [feedPosts]);

  useEffect(() => {
    trackEvent(FeedEvents.LOADED_FEED_PAGE, {
      isLoggedIn: !isLoggedOutState,
    });
  }, [isLoggedOutState]);

  const getComedian = useCallback(
    (id: string | undefined) => {
      if (id == null) return null;
      return comedians[id];
    },
    [comedians]
  );

  const generateScreenIntersectionCallback = useCallback(
    (postId: string) =>
      ([entry]: IntersectionObserverEntry[]) => {
        const { intersectionRatio } = entry;
        const top = entry.boundingClientRect.top;

        // update the positions of video cards in the feed
        setVideoCardPositions((prev) => {
          const newValue = { intersectionRatio, top };
          if (
            prev[postId]?.intersectionRatio === intersectionRatio &&
            prev[postId]?.top === top
          ) {
            return prev;
          }
          // otherwise, update this post's position
          return { ...prev, [postId]: newValue };
        });
      },
    []
  );

  const playingVideoPostId = useMemo(() => {
    const videoPostIds = Object.keys(videoCardPositions);

    // New approach: Find videos with reasonable visibility (>50%) and prioritize the most centered one
    const videoPostIdsInViewport = videoPostIds.filter(
      (postId) => videoCardPositions[postId].intersectionRatio > 0.5
    );

    if (videoPostIdsInViewport.length === 0) {
      return null;
    }

    if (videoPostIdsInViewport.length === 1) {
      return videoPostIdsInViewport[0];
    }

    // When multiple videos are visible, choose the one that's most centered in viewport
    // This means the video with the smallest absolute distance from center of screen
    const centerOfScreen = window.innerHeight / 2;

    const videoWithDistances = videoPostIdsInViewport.map((postId) => {
      const position = videoCardPositions[postId];
      // Estimate video center by adding half of estimated video height to top position
      const estimatedVideoCenter = position.top + window.innerHeight * 0.4; // rough estimate
      const distanceFromCenter = Math.abs(
        centerOfScreen - estimatedVideoCenter
      );

      return {
        postId,
        distanceFromCenter,
        intersectionRatio: position.intersectionRatio,
      };
    });

    // Sort by distance from center, then by intersection ratio as tiebreaker
    videoWithDistances.sort((a, b) => {
      if (Math.abs(a.distanceFromCenter - b.distanceFromCenter) < 50) {
        // If distances are very close, prefer higher intersection ratio
        return b.intersectionRatio - a.intersectionRatio;
      }
      return a.distanceFromCenter - b.distanceFromCenter;
    });

    const selectedVideo = videoWithDistances[0].postId;

    return selectedVideo;
  }, [videoCardPositions]);

  const renderPost = useCallback(
    (post: FeedPost) => {
      // Handle new placeholder types first
      if (post.type === FeedPostType.SUGGESTED_COMEDIANS_CAROUSEL) {
        if (!suggestedComedians || suggestedComedians.length === 0) return null;
        const filteredSuggestedComedians = suggestedComedians.filter(
          (comedian) => !locallyHiddenComedianIds.has(comedian.id)
        );
        if (filteredSuggestedComedians.length === 0) return null;
        return (
          <div
            className={cn(
              "relative lg:max-w-5xl lg:overflow-hidden",
              // The xl+ rail owns comedian discovery while the brand moment is on.
              brandMomentEnabled && "xl:hidden"
            )}
            key={post.id}
          >
            <SuggestedComedians
              comedians={filteredSuggestedComedians}
              isLoggedOutState={isLoggedOutState}
              hideEnabled={hideEnabled && !isLoggedOutState}
              onHide={handleComedianHide}
            />
          </div>
        );
      }
      if (post.type === FeedPostType.CONTINUE_WATCHING_CAROUSEL) {
        if (
          !continueWatchingSpecials ||
          continueWatchingSpecials.length === 0
        ) {
          return null;
        }
        return (
          <div className="relative lg:max-w-5xl" key={post.id}>
            <ContinueWatchingFeedCarousel specials={continueWatchingSpecials} />
          </div>
        );
      }
      if (post.type === FeedPostType.TRENDING_SPECIALS_CAROUSEL) {
        if (!trendingSpecials || trendingSpecials.length === 0) return null;
        return (
          <div className="relative lg:max-w-5xl" key={post.id}>
            <TrendingSpecialsFeed
              trendingSpecials={trendingSpecials}
              comedians={comedians}
            />
          </div>
        );
      }

      if (isLoading) {
        return <VideoFeedCardSkeleton key={`skeleton-${post.id}`} />;
      }

      if (post.type === FeedPostType.COMEDIAN) {
        const comedian = getComedian(post.comedian?.id);
        if (comedian == null) {
          const error = new Error(
            `Comedian with ID ${post.comedian?.id} is missing when rendering feed post of type ${FeedPostType.COMEDIAN}`
          );
          console.warn(error.message);
          logException(error);
          return null;
        }
        return (
          <div key={post.id} className="px-4 md:px-0">
            <FeaturedComedianFeedCard
              post={post}
              comedian={comedian}
              isLoggedOutState={isLoggedOutState}
              userFollowsComedian={post.userFollowsComedian ?? false}
              hideEnabled={hideEnabled && !isLoggedOutState}
              onHide={handlePostHide}
              pageTypeLabel={post.pageTypeLabel}
            />
          </div>
        );
      } else if (
        post.type == FeedPostType.SPECIAL ||
        post.type == FeedPostType.SUGGESTED_SPECIAL
      ) {
        if (post.special == null) {
          const error = new Error(
            `Special is missing when rendering feed post of type ${FeedPostType.SPECIAL} or ${FeedPostType.SUGGESTED_SPECIAL}`
          );
          console.warn(error.message);
          logException(error);
          return null;
        }
        const comedian = getComedian(post.special.comedian_id);
        if (comedian == null) {
          const error = new Error(
            `Comedian with ID ${post.special.comedian_id} is missing when rendering feed post of type ${FeedPostType.SPECIAL} or ${FeedPostType.SUGGESTED_SPECIAL}`
          );
          console.warn(error.message);
          logException(error);
          return null;
        }
        const screenIntersectionCallback = generateScreenIntersectionCallback(
          post.id
        );
        return (
          <VideoFeedCard
            post={post}
            isLoggedOutState={isLoggedOutState}
            key={post.id}
            comedian={comedian}
            special={post.special}
            userFollowsComedian={post.userFollowsComedian}
            muted={videosMuted}
            handleMutedChange={handleMutedChange}
            playing={playingVideoPostId === post.id}
            screenIntersectionCallback={screenIntersectionCallback}
            isMobileVideoLayoutEnabled={isMobileVideoLayoutEnabled}
            hideEnabled={hideEnabled && !isLoggedOutState}
            onHide={handlePostHide}
          />
        );
      } else if (post.type === FeedPostType.NEARBY_SHOWS) {
        return (
          <div
            className={cn(
              "px-4 md:px-0",
              // The xl+ rail owns shows-near-you while the brand moment is on.
              brandMomentEnabled && "xl:hidden"
            )}
            key={post.id}
          >
            <UpcomingShowsFeedUnitV2 brandMomentEnabled={brandMomentEnabled} />
          </div>
        );
      } else {
        const error = new Error(
          `Unknown feed unit type ${post.type} when rendering feed post`
        );
        console.warn(error.message);
        logException(error);
        return null;
      }
    },
    [
      getComedian,
      isLoggedOutState,
      brandMomentEnabled,
      generateScreenIntersectionCallback,
      videosMuted,
      playingVideoPostId,
      isLoading,
      suggestedComedians,
      continueWatchingSpecials,
      trendingSpecials,
      comedians,
      handleMutedChange,
      isMobileVideoLayoutEnabled,
      hideEnabled,
      handlePostHide,
      handleComedianHide,
      locallyHiddenComedianIds,
    ]
  );

  const incrementPostsToShow = useCallback(() => {
    if (postsToShow >= displayedPosts.length) {
      trackEvent(FeedEvents.REACHED_END_OF_FEED_CONTENT, {
        displayPostsCount: displayedPosts.length,
        totalPostsCount: displayedPosts.length,
      });
      return;
    }
    trackEvent(FeedEvents.REACHED_END_OF_FEED_PAGE, {
      displayPostsCount: postsToShow,
      totalPostsCount: displayedPosts.length,
    });
    setPostsToShow((prevPostsToShow) => prevPostsToShow + 10);
  }, [displayedPosts.length, postsToShow]);

  useEffect(() => {
    const currentRef = pageBottomRef.current;
    if (!currentRef) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (postsToShow < displayedPosts.length) {
            incrementPostsToShow();
          }
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(currentRef);
    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [incrementPostsToShow, postsToShow, displayedPosts.length]);

  return (
    <>
      {/* Render the posts provided by FeedWrapper (already filtered/ordered) */}
      {displayedPosts.slice(0, postsToShow).map(renderPost)}

      {/* Infinite scroll trigger & end message */}
      {postsToShow < displayedPosts.length && (
        <div ref={pageBottomRef} className="h-10" />
      )}
      {postsToShow >= displayedPosts.length && !isLoading && (
        <div className="py-4 text-center text-gray-500">
          No more posts to load
        </div>
      )}
    </>
  );
}
